import { useQuery } from '@tanstack/react-query'
import { stats } from '@/lib/api'
import { SteamIcon } from '@/components/PlatformIcons'

const STEAM_BLUE = '#66c0f4'

interface SteamGame {
  appId: number
  name: string
  playtimeForever: number
  playtimeRecent?: number
  iconUrl?: string
}

interface SteamData {
  steamId: string
  personaName: string
  avatarUrl: string
  games: SteamGame[]
  recentlyPlayed: SteamGame[]
}

export function SteamStats() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['stats', 'steam'],
    queryFn: async () => {
      const result = await stats.getSteam()
      return result.data as SteamData
    },
  })

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#66c0f4] border-t-transparent mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Loading Steam stats...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="container mx-auto max-w-5xl px-4 py-16 text-center">
        <h2 className="text-2xl font-display font-bold mb-2">Failed to load Steam stats</h2>
        <p className="text-gray-400 mb-6">Something went wrong fetching your Steam data.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
        >
          Try Again
        </button>
      </div>
    )
  }

  const games = data.games || []
  const recentlyPlayed = data.recentlyPlayed || []
  const totalGames = games.length
  const totalHours = Math.round(games.reduce((sum, g) => sum + g.playtimeForever, 0) / 60)
  const recentHours = Math.round(recentlyPlayed.reduce((sum, g) => sum + (g.playtimeRecent || 0), 0) / 60)
  const topGames = [...games].sort((a, b) => b.playtimeForever - a.playtimeForever).slice(0, 10)
  const maxPlaytime = topGames[0]?.playtimeForever || 1

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8 space-y-10">
      {/* Profile Header */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#66c0f4]/30 transition-colors p-6">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <img
            src={data.avatarUrl}
            alt={data.personaName}
            className="w-20 h-20 rounded-full border-2 border-[#66c0f4]/50"
          />
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
              <SteamIcon size={18} color={STEAM_BLUE} />
              <h1 className="text-2xl font-display font-bold">{data.personaName}</h1>
            </div>
            <p className="text-gray-500 text-xs font-mono mb-3">{data.steamId}</p>
            <div className="flex flex-wrap justify-center sm:justify-start gap-6">
              <QuickStat label="Games Owned" value={totalGames.toLocaleString()} />
              <QuickStat label="Total Hours" value={totalHours.toLocaleString()} />
              <QuickStat label="Recent Hours" value={`${recentHours}h`} />
            </div>
          </div>
        </div>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <BigStat label="Library Size" value={totalGames.toLocaleString()} />
        <BigStat label="Total Playtime" value={`${totalHours.toLocaleString()}h`} />
        <BigStat label="Recent Activity" value={`${recentHours}h`} sub="Last 2 weeks" />
        <BigStat label="Games Played Recently" value={recentlyPlayed.length.toString()} />
      </div>

      {/* Top Games by Playtime */}
      {topGames.length > 0 && (
        <section>
          <h2 className="text-xl font-display font-bold mb-4">Top Games by Playtime</h2>
          <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#66c0f4]/30 transition-colors divide-y divide-gray-800">
            {topGames.map((game, i) => {
              const hours = Math.round(game.playtimeForever / 60)
              const pct = (game.playtimeForever / maxPlaytime) * 100
              return (
                <div key={game.appId} className="flex items-center gap-4 p-3">
                  <span className="text-gray-500 text-sm font-mono w-6 text-right shrink-0">
                    {i + 1}
                  </span>
                  <img
                    src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${game.appId}/header.jpg`}
                    alt={game.name}
                    className="w-16 h-8 rounded object-cover shrink-0 bg-gray-700"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.visibility = 'hidden'
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{game.name}</p>
                    <div className="mt-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, backgroundColor: STEAM_BLUE }}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-gray-400 shrink-0 tabular-nums">
                    {hours.toLocaleString()}h
                  </span>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Recently Played */}
      {recentlyPlayed.length > 0 && (
        <section>
          <h2 className="text-xl font-display font-bold mb-4">Recently Played</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentlyPlayed.slice(0, 6).map((game) => {
              const hours = Math.round(game.playtimeForever / 60)
              const recentH = game.playtimeRecent ? Math.round(game.playtimeRecent / 60) : 0
              return (
                <div
                  key={game.appId}
                  className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#66c0f4]/30 transition-colors overflow-hidden"
                >
                  <img
                    src={`https://cdn.cloudflare.steamstatic.com/steam/apps/${game.appId}/header.jpg`}
                    alt={game.name}
                    className="w-full h-24 object-cover bg-gray-700"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.visibility = 'hidden'
                    }}
                  />
                  <div className="p-3">
                    <p className="text-sm font-medium truncate mb-1">{game.name}</p>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-gray-400">{hours.toLocaleString()}h total</span>
                      {recentH > 0 && (
                        <span className="text-xs" style={{ color: STEAM_BLUE }}>
                          {recentH}h recent
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}
    </div>
  )
}

function QuickStat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <span className="text-lg font-bold text-white">{value}</span>
      <span className="text-xs text-gray-500 ml-1.5">{label}</span>
    </div>
  )
}

function BigStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#66c0f4]/30 transition-colors p-5">
      <p className="text-2xl font-display font-bold mb-1">{value}</p>
      <p className="text-sm text-gray-400">{label}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  )
}
